import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import RegisterModal from '../../components/Auth/RegisterModal';
import LoginModal from '../../components/Auth/LoginModal';
import AiAskBox from '../../components/AiAskBox/AiAskBox';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import usePickerLanguage from '../ReadingTypes/usePickerLanguage';
import { ensureTarotScripts, tarotScriptsReady } from '../ReadingTypes/loadTarotScripts';
import './FreeTarot.css';

const FreeTarot = () => {
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  usePickerLanguage('free');

  useEffect(() => {
    // Ensure styles are loaded
    if (!document.getElementById('tarot-reader-styles')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/tarot-reader/cards-only.css';
      link.id = 'tarot-reader-styles';
      document.head.appendChild(link);
    }

    let cancelled = false;
    const alreadyLoaded = tarotScriptsReady();
    ensureTarotScripts()
      .then(() => {
        if (cancelled) return;
        if (alreadyLoaded) window.initTarotApp();
      })
      .catch((error) => {
        console.error('Failed to load tarot scripts:', error);
      });

    return () => {
      cancelled = true;
      if (window.tarotApp) {
        window.tarotApp = null;
      }
      if (typeof window.cleanupTarotDeck === 'function') {
        window.cleanupTarotDeck();
      }
    };
  }, []);

  const freeTarotJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How does a free online tarot reading work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You select a spread, then choose cards from our virtual 78-card deck. Each card you pick is interpreted based on its position and meaning. Your reading is personalized to the exact cards and order you select."
        }
      },
      {
        "@type": "Question",
        "name": "How do reversed cards work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Reversed cards have different meanings than upright cards — they often indicate blocked energy, delayed outcomes, or internal reflection. When enabled, you choose the percentage chance each card will appear reversed."
        }
      },
      {
        "@type": "Question",
        "name": "Is this tarot reading really free?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! All readings on this page are 100% free with no signup required."
        }
      }
    ]
  };

  return (
    <div className="free-tarot-page">
      <Helmet>
        <title>Free Tarot Reading Online - Celtic Cross | NowTarot AI</title>
        <meta name="description" content="Get a 100% free Celtic Cross tarot reading online. 10-card spread with instant AI-powered interpretations, no signup required." />
        <meta name="keywords" content="free tarot reading, online tarot reading, celtic cross tarot, tarot card reading free, tarot spreads, past present future tarot" />
        <link rel="canonical" href="https://nowtarot.com/free-tarot/" />
        <meta property="og:title" content="Free Tarot Reading Online - Celtic Cross | NowTarot AI" />
        <meta property="og:description" content="Get a 100% free Celtic Cross tarot reading. Instant AI interpretations, no signup required." />
        <meta property="og:image" content="https://nowtarot.com/og-free-tarot.png" />
        <meta property="og:url" content="https://nowtarot.com/free-tarot/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Tarot Reading Online - Celtic Cross" />
        <meta name="twitter:description" content="Get a 100% free Celtic Cross tarot reading. No signup required." />
        <meta name="twitter:image" content="https://nowtarot.com/og-free-tarot.png" />
        <script type="application/ld+json">{JSON.stringify(freeTarotJsonLd)}</script>
      </Helmet>
      <Header />
      <div className="stars"></div>
      <div className="container">
        <header className="tarot-header">
          <h1 className="page-title">Free Tarot Reading</h1>
        </header>

        <section className="intro-section">
          <p>
            Get a 100% free Celtic Cross Tarot reading with clear, accurate insight. Pick your cards and get
            instant answers from the Universe.
          </p>
        </section>

        {/* Main Content */}
        <main className="main-content">
          {/* Deck Area */}
          <section className="deck-section visible" id="deckSection">
            
            {/* Hidden: spread defaults to Celtic Cross Classic */}
            <div className="spread-select-wrap" style={{ display: 'none' }}>
              <label htmlFor="spreadSelect">Choose your spread:</label>
              <select id="spreadSelect" className="spread-select">
                {/* Options will be generated by JS */}
              </select>
            </div>
            
            <p className="cards-needed" id="cardsNeeded">Please choose 10 Tarot cards for your reading!</p>
            
            {/* Hidden: reversed cards disabled by default */}
            <div className="options-inline" style={{ display: 'none' }}>
              <label className="toggle-label">
                <input type="checkbox" id="enableReversed" className="toggle-checkbox" />
                <span className="toggle-switch"></span>
                <span>Enable Reversed Cards</span>
              </label>
              <div className="slider-container" id="sliderContainer">
                <label htmlFor="reversedChance">Chance: <span id="chanceValue">25</span>%</label>
                <input type="range" id="reversedChance" min="5" max="50" defaultValue="25" className="slider" />
              </div>
            </div>
            
            <div id="cardarea-wrap" className="cardarea-wrap">
              <div id="cardarea">
                {/* Card deck will be generated by JS */}
              </div>
            </div>
            
            <p className="deck-instruction" id="deckInstruction"><em>Click on the cards for your reading!</em></p>
            
            <div className="deck-controls deck-controls-single">
              <button type="button" className="btn btn-reset" id="shuffle-again">
                Shuffle Again
              </button>
            </div>
            
            <button className="btn btn-interpret" id="interpretBtn" disabled style={{display: 'none'}}>
              ✨ Interpret Your Cards ✨
            </button>
          </section>

          {/* Interpretation Display */}
          <section className="interpretation-section" id="interpretationSection">
            <h3>Your Reading Interpretation</h3>
            <div className="interpretation-content" id="interpretationContent">
              {/* Interpretations will be displayed here */}
            </div>
            <button className="btn btn-new-reading" id="newReadingBtn">
              Start a New Reading
            </button>
          </section>

          <section className="cta-section ft-ask-band">
            <h3>Ask the AI tarot</h3>
            <p>Type a question. Get cards and an answer — no signup.</p>
            <AiAskBox onSignUp={() => setShowRegister(true)} />
          </section>

          <FeaturePromo variant="page" seed="/free-tarot/" exclude="/free-tarot/" />

          {/* About Readings */}
          <section className="about-section">
            <h3>About Our Tarot Readings</h3>
            <div className="readings-info">
              <a href="/celtic-cross-tarot/" className="reading-type reading-type-link">
                <h4>Celtic Cross Tarot Reading →</h4>
                <p>The Celtic Cross is a 10-card spread and one of the most popular tarot layouts. It explores your present situation, past influences, hopes, fears, external forces, and likely outcome.</p>
              </a>
              <a href="/yes-or-no-tarot/" className="reading-type reading-type-link">
                <h4>Yes or No Tarot →</h4>
                <p>One card for a direct question. A free yes or no tarot reading, with no signup.</p>
              </a>
              <a href="/love-tarot-reading/" className="reading-type reading-type-link">
                <h4>Love Tarot Reading →</h4>
                <p>Where the relationship has been, where it is, and where it is heading.</p>
              </a>
              <a href="/three-card-tarot/" className="reading-type reading-type-link">
                <h4>3-Card Past, Present, Future →</h4>
                <p>A free 3 card tarot spread for a specific question, with a clear past, present, and future.</p>
              </a>
              <a href="/card-of-the-day/" className="reading-type reading-type-link">
                <h4>Tarot Card of the Day →</h4>
                <p>Shuffle the deck and draw one card as the theme for today.</p>
              </a>
              <a href="/six-card-tarot-spread/" className="reading-type reading-type-link">
                <h4>6-Card Universal Spread →</h4>
                <p>How you feel, what you desire and fear, what is working for and against you, and the likely outcome.</p>
              </a>
              <a href="/daily-tarot-reading/" className="reading-type reading-type-link">
                <h4>Daily Tarot Reading →</h4>
                <p>Pick one card each morning to reveal the energy and guidance for your day ahead.</p>
              </a>
              <a href="/mind-body-spirit-tarot/" className="reading-type reading-type-link">
                <h4>Mind Body Spirit Reading →</h4>
                <p>A 3-card wellness check-in for your thoughts, physical well-being, and spiritual path.</p>
              </a>
              <a href="/new-love-tarot-reading/" className="reading-type reading-type-link">
                <h4>New Love Reading →</h4>
                <p>For singles: how ready you are for love, where it may appear, and what to overcome first.</p>
              </a>
              <a href="/yes-or-no-tarot/" className="reading-type reading-type-link">
                <h4>Yes or No Reading →</h4>
                <p>A single card reading that offers a clear yes or no answer. Focus on your question and draw one card for direct guidance.</p>
              </a>
              <a href="/love-tarot-reading/" className="reading-type reading-type-link">
                <h4>Love Tarot Reading →</h4>
                <p>A 3-card relationship spread exploring your romantic past, present situation, and where your love life is heading.</p>
              </a>
              <a href="/career-tarot-reading/" className="reading-type reading-type-link">
                <h4>Career &amp; Money Reading →</h4>
                <p>A focused spread for work and finances: your current situation, the obstacles in your way, and guidance toward prosperity.</p>
              </a>
              <a href="/tarot-card-meanings/" className="reading-type reading-type-link">
                <h4>Tarot Card Meanings →</h4>
                <p>Study all 78 cards: upright and reversed meanings, plus what each card says about love, career, and money.</p>
              </a>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="faq-section">
            <h3>Frequently Asked Questions</h3>
            <div className="faq-list">
              <div className="faq-item">
                <h4>How does a free online tarot reading work?</h4>
                <p>You select a spread, then choose cards from our virtual 78-card deck. Each card you pick is interpreted based on its position and meaning. Your reading is personalized to the exact cards and order you select.</p>
              </div>
              <div className="faq-item">
                <h4>How do reversed cards work?</h4>
                <p>Reversed cards have different meanings than upright cards—they often indicate blocked energy, delayed outcomes, or internal reflection. When enabled, you choose the percentage chance each card will appear reversed.</p>
              </div>
              <div className="faq-item">
                <h4>Is this tarot reading really free?</h4>
                <p>Yes! All readings on this page are 100% free with no signup required.</p>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
      </div>
      <Footer />
      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}
    </div>
  );
};

export default FreeTarot;
